import { TelemetryInfo } from "@/hooks/use-telemetry-info";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/**
 * Standalone page config — fires "Page Loaded" (identical to PHP CRM)
 */
export type StandalonePageConfig = {
  mode: "standalone";
  pageName: string;
  category: string;
  subcategory: string;
  action: string;
  viewType?: string;
  period?: string;
};

/**
 * Embedded view config — fires "Embedded View Loaded" instead of "Page Loaded"
 * Used for pages loaded inside an iframe within the PHP CRM
 */
export type EmbeddedViewConfig = {
  mode: "embedded";
  viewName: string;
};

// ---------------------------------------------------------------------------
// Return types
// ---------------------------------------------------------------------------

type AuthProperties = {
  user_id: string;
  user_type: string;
  user_role: string;
  user_email: string;
  user_name: string;
  company_id: number;
  company_name: string;
  company_email: string;
  company_package_name: string;
  company_package_level: number;
  company_is_active: boolean | undefined; // Derived from AccountStatus enum
};

type ContextProperties = {
  device_source?: string;
  is_mobile?: boolean;
  env: string;
};

type StandalonePageProperties = {
  event_type: string;
  path: string;
  referrer: string;
  status: string;
  url: string;
  page_name?: string;
  category?: string;
  subcategory?: string;
  action?: string;
  view_type?: string;
  period?: string;
};

type EmbeddedViewProperties = {
  view_name: string;
  path: string;
};

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

export const ROLE_NAME_MAP: Record<string, string> = {
  OWNER: "Owner",
  ADMIN: "Admin",
  EMPLOYEE: "Employee",
};

// ---------------------------------------------------------------------------
// Pure functions
// ---------------------------------------------------------------------------

/**
 * Normalize a path by removing dynamic IDs and trailing slashes.
 * Example: /customers/details/123 -> /customers/details
 */
export const normalizePath = (path: string): string => {
  if (!path || path === "/") return path;

  let normalized = path.replace(/\/(edit|details)\/\d+$/, "/$1");

  if (normalized !== "/") {
    normalized = normalized.replace(/\/$/, "");
  }

  return normalized;
};

export const buildAuthProperties = (
  telemetryInfo: TelemetryInfo | null,
): Partial<AuthProperties> => {
  if (!telemetryInfo) return {};

  const { user, company } = telemetryInfo;
  if (!user) return {};

  return {
    user_id: `cli_${user.id}`,
    user_type: "Client",
    user_role: ROLE_NAME_MAP[user.role] || user.role,
    user_email: user.email,
    user_name: `${user.firstName} ${user.lastName}`.trim(),
    company_id: company.id,
    company_name: company.name,
    company_email: company.email,
    company_package_name: company.subscription?.plan.name,
    company_package_level: company.subscription?.tier.level,
    company_is_active:
      company.accountStatus != null
        ? company.accountStatus === "ACTIVE"
        : undefined,
  };
};

type BuildContextParams = {
  deviceSource: string | null;
  /** Sourced from the same breakpoint as `useMobileSize()` — omitted (not `false`) when unknown, e.g. in a non-browser context. */
  isMobile: boolean | null;
  env: string;
};

export const buildContextProperties = ({
  deviceSource,
  isMobile,
  env,
}: BuildContextParams): ContextProperties => {
  return {
    ...(deviceSource && { device_source: deviceSource }),
    ...(isMobile != null && { is_mobile: isMobile }),
    env,
  };
};

type BuildEventParams = {
  telemetryInfo: TelemetryInfo | null;
  deviceSource: string | null;
  isMobile: boolean | null;
  env: string;
  eventProperties?: Record<string, any>;
};

/**
 * Build merged event properties.
 * Merging order: defaults → auth → context → event (event wins).
 */
export const buildEventProperties = ({
  telemetryInfo,
  deviceSource,
  isMobile,
  env,
  eventProperties,
}: BuildEventParams): Record<string, any> => {
  return {
    category: "none",
    subcategory: "none",
    status: "none",
    ...buildAuthProperties(telemetryInfo),
    ...buildContextProperties({ deviceSource, isMobile, env }),
    ...eventProperties,
  };
};

type BuildStandalonePageParams = {
  config: StandalonePageConfig | null;
  normalizedPath: string;
  deviceSource: string | null;
};

export const buildStandalonePageProperties = ({
  config,
  normalizedPath,
  deviceSource,
}: BuildStandalonePageParams): StandalonePageProperties => {
  return {
    event_type: "page_load_event",
    path: normalizedPath,
    referrer: deviceSource || "direct",
    status: "success",
    ...(config && {
      page_name: config.pageName,
      category: config.category,
      subcategory: config.subcategory,
      action: config.action,
      ...(config.viewType && { view_type: config.viewType }),
      ...(config.period && { period: config.period }),
    }),
    url: normalizedPath,
  };
};

type BuildEmbeddedViewParams = {
  config: EmbeddedViewConfig;
  normalizedPath: string;
};

export const buildEmbeddedViewProperties = ({
  config,
  normalizedPath,
}: BuildEmbeddedViewParams): EmbeddedViewProperties => {
  return {
    view_name: config.viewName,
    path: normalizedPath,
  };
};
